import { useRecoilValue } from "recoil";
import {
  QrPageHeaderLeft,
  QrPageHeaderRight,
  QrPageHeader,
  QrPageWrapper,
  QrSeedWrapper,
  QrWrapper,
  QrUserNameWrapper,
  QrUserInfoItem,
  QrUserInfoWrapper,
  QrExpiredWrapper,
} from "./styles";
import { insetState } from "../../../../../recoil/inset";
import { UserInfo } from "../../../../../api/auth";
import PubQR from "../pub-qr";
import { useEffect, useState } from "react";
import { myNearPubPoint } from "../../../../../api/member";
import useGeoLocation from "../../../../../hooks/useGeoLocation";
import { useHistory } from "react-router-dom";

interface QrPageProps {
  user: UserInfo;
  qrValue: string;
  getRemainingSeconds: (expireTime: number) => number;
  setIsQrOpen: (isQrOpen: boolean) => void;
}

const QrPage = ({
  user,
  qrValue,
  getRemainingSeconds,
  setIsQrOpen,
}: QrPageProps) => {
  const history = useHistory();
  const inset = useRecoilValue(insetState);
  const [expiredText, setExpiredText] = useState("");
  const { latitude, longitude } = useGeoLocation();
  const [pubName, setPubName] = useState("");
  const [pubPoint, setPubPoint] = useState(0);

  // 암호화 함수
  async function encryptData(data: any, secretKey: any) {
    // JSON 객체를 문자열로 변환
    const jsonString = JSON.stringify(data);

    // 텍스트를 UTF-8 인코딩
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(jsonString);

    // 비밀키를 CryptoKey로 가져오기
    const key = await window.crypto.subtle.importKey(
      "raw",
      secretKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    // 랜덤 초기화 벡터(IV) 생성
    const iv = window.crypto.getRandomValues(new Uint8Array(12));

    // 암호화 수행
    const encryptedContent = await window.crypto.subtle.encrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      key,
      dataBuffer
    );

    // 암호화된 데이터와 IV를 결합
    const encryptedData = new Uint8Array(
      iv.length + encryptedContent.byteLength
    );
    encryptedData.set(iv);
    encryptedData.set(new Uint8Array(encryptedContent), iv.length);

    // Base64로 인코딩
    return btoa(String.fromCharCode.apply(null, Array.from(encryptedData)));
  }

  async function decryptData(encryptedBase64: string, secretKey: any) {
    // Base64 디코딩
    const encryptedData = new Uint8Array(
      atob(encryptedBase64)
        .split("")
        .map((char) => char.charCodeAt(0))
    );

    // IV 추출 (첫 12바이트)
    const iv = encryptedData.slice(0, 12);
    const data = encryptedData.slice(12);

    // 비밀키를 CryptoKey로 가져오기
    const key = await window.crypto.subtle.importKey(
      "raw",
      secretKey,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"]
    );

    // 복호화 수행
    const decryptedContent = await window.crypto.subtle.decrypt(
      {
        name: "AES-GCM",
        iv: iv,
      },
      key,
      data
    );

    // 복호화된 데이터를 문자열로 변환
    const decoder = new TextDecoder();
    const decryptedString = decoder.decode(decryptedContent);

    // JSON으로 파싱
    return JSON.parse(decryptedString);
  }

  const secretKey = new Uint8Array([
    1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21,
    22, 23, 24, 25, 26, 27, 28, 29, 30, 31, 32,
  ]);

  useEffect(() => {
    const updateExpiredText = async () => {
      const decrypted = await decryptData(qrValue, secretKey);
      const remaining = getRemainingSeconds(decrypted.expire);
      if (remaining > 0) {
        setExpiredText(`${remaining}`);
      }
    };

    // 즉시 실행
    if (user) {
      updateExpiredText();

      // 매초마다 업데이트
      const interval = setInterval(updateExpiredText, 1000);

      return () => clearInterval(interval);
    }
  }, [qrValue, getRemainingSeconds]);

  //YY/MM/DD
  const formatBirthday = (value: string | undefined) => {
    if (!value) return "-";

    const [yyyy, mm, dd] = value.split("-");
    return `${yyyy.slice(-2)}/${mm}/${dd}`;
  };

  const formatPhoneNumber = (value: string | undefined) => {
    if (!value) return "-";

    const cleanNumber = value.replace(/\D/g, "");
    return cleanNumber.replace(/^(\d{3})(\d{4})(\d{4})$/, "$1-$2-$3");
  };

  const getNearPubPoint = async () => {
    const response = await myNearPubPoint(latitude, longitude);
    setPubName(response.pubName);
    setPubPoint(response.point);
  };

  useEffect(() => {
    getNearPubPoint();
  }, []);

  return (
    <QrPageWrapper inset={inset}>
      <QrPageHeader>
        <QrPageHeaderLeft>
          <img
            src="/image-web/Icon/Back.svg"
            alt="qr"
            onClick={() => setIsQrOpen(false)}
          />
          <div>QR코드 스캔</div>
        </QrPageHeaderLeft>
        <QrPageHeaderRight></QrPageHeaderRight>
      </QrPageHeader>
      <QrSeedWrapper>
        <div className="title">보유래빗</div>
        <div className="balance-wrapper">
          <div className="balance">{user.seed} Rabbit</div>
          <div className="bar">|</div>
          <div className="point">
            {pubName + " "}
            <span> {pubPoint / 10000} E</span>
            <img
              src="/image-web/home/right-arrow.svg"
              alt="arrow"
              onClick={() => {
                setIsQrOpen(false);
                history.push(`/wallet`);
              }}
            />
          </div>
        </div>
      </QrSeedWrapper>
      <QrWrapper onClick={() => setIsQrOpen(false)}>
        <PubQR qrValue={JSON.stringify(qrValue)} width="100%" height="100%" />
        <QrExpiredWrapper>{expiredText}</QrExpiredWrapper>
      </QrWrapper>

      <QrUserNameWrapper>
        <div className="title">이름</div>
        <div className="value">{user.username}</div>
      </QrUserNameWrapper>

      <QrUserInfoWrapper>
        <QrUserInfoItem>
          <div className="title">생년월일</div>
          <div className="value">{formatBirthday(user.birthday)}</div>
        </QrUserInfoItem>
        <QrUserInfoItem>
          <div className="title">전화번호</div>
          <div className="value">{formatPhoneNumber(user.phoneNumber)}</div>
        </QrUserInfoItem>
      </QrUserInfoWrapper>
    </QrPageWrapper>
  );
};

export default QrPage;
